<script lang="ts">
	import { resetInput } from '$lib/utils/forms';
	import type { InputFilesProps } from '../types';
	import { attrValue, shortId } from '$lib/utils/html';
	import Input from '../Input.svelte';
	import Icon from '../../Icon.svelte';
	import { t } from 'svelte-i18n';
	import { uploadPreview } from '$lib/actions';
	import MediaBlock from '../../MediaBlock.svelte';
	import { dropAction } from '$lib/actions';
	import { viewport } from '$lib/stores';
	import ButtonText from '../../buttons/variants/ButtonText.svelte';
	import { fileByteFormatIsAccepted, isFileTypeAccepted } from '@app/services/files';
	import TagError from '$lib/elements/tags/variants/TagError.svelte';
	import TagSuccess from '$lib/elements/tags/variants/TagSuccess.svelte';
	import TagNeutral from '$lib/elements/tags/variants/TagNeutral.svelte';

	export let props: InputFilesProps;

	const imageType = /^image\//;
	let dragover = false;
	let isErrorDisplayed: boolean;

	const handleChange = () => {
		if (!props.dirty) {
			props.dirty = true;
		}
	};

	const handleChanges = async (files: FileList | null) => {
		if (files) {
			let currentFile = files[0];
			if (props.accept) {
				if (!isFileTypeAccepted(props, currentFile)) {
					files = null;
					props.error = `${$t('kyc.steps.documents.additionalContent.format.invalid')}
					 ${$t('kyc.steps.documents.additionalContent.format.title')} : ${$t(
							'kyc.steps.documents.additionalContent.format.value'
						)}`;
				} else {
					if (!(await fileByteFormatIsAccepted(props, currentFile))) {
						files = null;
						props.error = `${$t(
							'kyc.steps.documents.additionalContent.format.fileCorruptionMessage'
						)}`;
					} else {
						props.error = '';
					}
				}
			}
			props = Object.assign(props, {
				value: files,
				touched: true,
				dirty: true
			});
		}
	};

	const handleFocus = () => {
		if (!props.touched) {
			props.touched = true;
		}
	};

	$: if (props) {
		props.endingIcon = 'upload_directory';
	}

	$: required = props && props.required;
	$: disabled = props && props.disabled;
	$: accept = props && props.accept;
	$: multiple = props && props.multiple;
	$: selected = props?.value && props.value.length;

	let modalPictureTaker: any | null = null;

	let modalSrc: string | null | URL | RequestInfo = null;

	const handleUsePicture = async () => {
		const blob = await (await fetch(modalSrc)).blob();
		const file = new File([blob], `file_${shortId()}.jpeg`, {
			type: 'image/jpeg',
			lastModified: new Date().getTime()
		});
		if (props.multiple && props.value) {
			props.value = [...props.value, file] as FileList;
		} else {
			props.value = [file] as FileList;
		}
		modalPictureTaker = null;
	};

	const clearInput = () => {
		props = resetInput(props);
	};
</script>

<Input bind:props>
	{#if props.readonly && props?.state !== 'INVALID'}
		<div class="items-center">
			{#if props.state === 'VALID'}
				<TagSuccess
					props={{
						iconLeft: 'check',
						label: $t('profile.section.documents.docState.valid.tag'),
						classNames: 'mr-2'
					}}
				/>
				<div>{$t('profile.section.documents.docState.valid.label')}</div>
			{:else}
				<TagNeutral
					props={{
						label: $t('profile.section.documents.docState.pending.tag'),
						classNames: 'mr-2',
						iconLeft: 'clock'
					}}
				/>
				<div>{$t('profile.section.documents.docState.pending.label')}</div>
			{/if}
		</div>
	{:else}
		<div>
			{#if props.readonly && props.state === 'INVALID'}
				<TagError
					props={{
						label: $t('profile.section.documents.docState.invalid.tag'),
						classNames: 'mr-2'
					}}
				/>
			{/if}
			<!-- svelte-ignore a11y-click-events-have-key-events -->
			<!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
			<label
				for={props.id}
				class={attrValue([
					`${props?.classNames} cursor-pointer flex items-center justify-center flex-row border-black border-dashed border rounded-lg space-x-2 p-4`
				])}
				class:dragover
				class:border-danger-light={isErrorDisplayed}
				class:disabled={props.disabled}
				use:dropAction={!disabled}
				on:click|once={() => handleFocus()}
				on:highlight={() => (dragover = true)}
				on:unhighlight={() => (dragover = false)}
				on:dropped={(e) => handleChanges(e.detail)}
			>
				<Icon name={props.endingIcon} classNames="text-secondary mr-3" size="big" />
				<div>
					{#if $viewport.isDesktop}
						<p>
							<span class="text-primary text-lg font-bold underline">{$t('kyc.select')}</span>
							{$t('portfolio.export.extra')}
							<span class="text-primary text-lg font-bold">{$t('kyc.drag&drop')}</span>
						</p>
					{:else}
						<span class="text-primary text-lg font-bold underline">{$t('kyc.selectFolder')}</span>
					{/if}

					<p class="p-1 text-sm">Type de fichier : PDF, JPG, JPEG et PNG</p>
				</div>
			</label>
			<input
				{required}
				{disabled}
				{accept}
				{multiple}
				id={props.id}
				type="file"
				name={props.name}
				on:change={(e) => handleChanges(e.target.files)}
				on:keypress
				on:keyup
				on:input
				on:input|once={() => handleChange()}
			/>
		</div>
		{#if props.showPreview && selected}
			<div class="space-y-1 mt-2">
				{#each props.value as file}
					<MediaBlock classNames=" px-3 py-2 space-x-3 rounded-lg flex items-center">
						<div class="w-full flex items-center gap-3" slot="start">
							{#if imageType.test(file.type)}
								<div class="w-[50px] h-[50px]">
									<img use:uploadPreview={file} alt="" class="object-cover w-full" />
								</div>
							{:else}
								<Icon name="download" />
							{/if}
							<div class="truncate">{file.name}</div>
							<ButtonText props={{ iconLeft: 'cross' }} on:click={() => clearInput()} />
						</div>
					</MediaBlock>
				{/each}
			</div>
		{/if}
	{/if}
</Input>

<svelte:component
	this={modalPictureTaker}
	on:closeModal={() => (modalPictureTaker = null)}
	on:usePicture={() => handleUsePicture()}
	bind:imgSrc={modalSrc}
/>

<style lang="postcss">
	input {
		@apply hidden;
	}

	.input {
		@apply px-3 py-2 flex items-center;
	}

	.dragover {
		@apply border-primary bg-grey-lighter;
	}
</style>
